/**
 * Phase EU.1.6 — viewer-aware permission hooks.
 *
 * Backend enforces the viewer write-gate via middleware (see
 * backend/app/core/permissions.py). These hooks gate UI affordances so
 * disabled-by-the-server actions never even get presented.
 *
 * Read-only-with-DeepFLD-write semantics:
 *   - viewer + no feature  → cannot write
 *   - viewer + 'deepfld'   → can write
 *   - any other role       → can write everything
 */
import { useAuth, type AppRole } from './AuthProvider';

/** Roles that act as external (non-employee) users with restricted scope.
 *  viewer = generic external demo (Phase EU.1).
 *  verofy = Verofy parent-company customers (Phase EU.2.5c — sibling of viewer
 *  for namespace separation; same permissions today, may diverge later). */
export const EXTERNAL_ROLES: ReadonlySet<AppRole> = new Set<AppRole>(['viewer', 'verofy']);

/** Tabs/portals hidden from viewer accounts (DeepTrace, DeepRoads). */
export const VIEWER_HIDDEN_VIEWS: ReadonlySet<string> = new Set([
  'deeptrace',
  'deeproads',
]);

export type WriteFeature = 'deepfld';

/**
 * True if the current user is permitted to write. Pass an optional `feature`
 * to opt the user in for a specific tool (currently only 'deepfld').
 *
 * @example
 *   const canWrite = useCanWrite();           // false for viewer
 *   const canDesign = useCanWrite('deepfld'); // true for viewer
 */
export function useCanWrite(feature?: WriteFeature): boolean {
  const { role } = useAuth();
  if (!EXTERNAL_ROLES.has(role)) return true;
  return feature === 'deepfld';
}

/** True if the current user is in an external read-only demo role
 *  (viewer or verofy). */
export function useIsViewer(): boolean {
  return EXTERNAL_ROLES.has(useAuth().role);
}

/**
 * True if a named view/tab should be hidden from the current user.
 * Currently used to hide DeepTrace + DeepRoads tabs for viewers.
 *
 * @example
 *   const hideTrace = useViewIsHidden('deeptrace');
 */
export function useViewIsHidden(view: string): boolean {
  const isViewer = useIsViewer();
  return isViewer && VIEWER_HIDDEN_VIEWS.has(view);
}

/** Standard tooltip text for write affordances disabled by viewer status. */
export const READ_ONLY_TOOLTIP =
  'Read-only access — contact your admin to make changes.';

/** Roles that can browse the Reference portal (engineering standards + how-tos). */
const REFERENCE_ROLES: ReadonlySet<AppRole> = new Set<AppRole>([
  'admin', 'developer', 'lead', 'field_engineer', 'permitting',
  'permit_lead', 'permit_staff', 'pole_permit_lead', 'pole_permit_staff',
  'other_permit_lead', 'other_permit_staff', 'production',
]);

/** True if the current user can access the /reference portal. */
export function useCanReadReference(): boolean {
  const { role } = useAuth();
  return REFERENCE_ROLES.has(role as AppRole);
}

/** Domains whose SSO users receive a scoped nav: Market Intel + DeepDesign + Vault only.
 *  These users are admins in the DB (DOMAIN_DEFAULT_ROLES) but should not see internal
 *  operational portals (Admin, Connector, Lead, Construction, etc.).
 *  See wiki/entities/vero-access-model.md for the full access model and scale roadmap. */
const VERO_SSO_DOMAINS = new Set(['verobroadband.com', 'verofiber.com']);

/** True if the current user authenticated via a Vero SSO domain. */
export function useIsVeroUser(): boolean {
  const { user } = useAuth();
  const domain = user?.email?.split('@')[1]?.toLowerCase();
  return Boolean(domain && VERO_SSO_DOMAINS.has(domain));
}
